// ============================================================
// MANIC MEETER — Game State & Core Logic (Browser Edition)
// ============================================================

import {
  GameState,
  Player,
  PlayerRole,
  Meeting,
  TurnResult,
  DayStats,
  MeetingType,
  MAX_FATIGUE,
  MIN_WAGE,
  STARTING_SALARY,
  SLEEP_SALARY_PENALTY,
  CAUGHT_SALARY_PENALTY,
  PROCRASTINATE_FATIGUE_REDUCTION,
  WRONG_ANSWER_EXTRA_ROUNDS,
  MONOLOGUE_EXTRA_ROUNDS,
  SpecialEventType,
  PLAYER_ROLE_DIFFICULTY,
  ONLINE_ADDRESS_MODIFIER,
  FATIGUE_REDUCTION_CORRECT_ANSWER,
  MetaProgression,
  StatUpgradeId,
  STAT_UPGRADE_SPECS,
  ActivePowerup,
  PowerupId,
  MEETING_COMPLETION_FATIGUE_REDUCTION,
  DAY_CHANGE_FATIGUE_RESTORE_PERCENT,
  PerkModifiers,
  AchievementId,
  AchievementTracker,
  SessionStats,
  ColleagueRelationship,
  BingoCard,
  BingoGoal,
  BINGO_GOAL_SPECS,
  EndingType,
  PermanentUnlockId
} from './types';
import { generateMeeting, generateDaySchedule } from './meetings';
import { rollSpecialEvent } from './events';
import { procrastinationActivities, type AddressMethod } from './events';
import { C } from './i18n';

export function getStatEffect(meta: MetaProgression, id: StatUpgradeId): number {
  const def = STAT_UPGRADE_SPECS.find((u) => u.id === id)!;
  return meta.statLevels[id] * def.effectPerLevel;
}

export function createDayStats(salary: number): DayStats {
  return {
    meetingsSurvived: 0,
    timesCaught: 0,
    timesFellAsleep: 0,
    minigamesWon: 0,
    minigamesLost: 0,
    salaryStart: salary,
    salaryEnd: salary,
    meetingsSkipped: 0,
    monologues: 0,
    meetingTypesThisDay: [],
    bingoMeetingSurvived: false,
    bingoNoSleep: true,
    bingoNoCatch: true,
    bingoMinigameWon: false,
    bingoMonologue: false,
    bingoInvisible: false,
    bingoQuestionCorrect: false,
    bingoProcrastinateCount: 0,
    bingoDirectionChange: false
  };
}

export function createPlayer(name: string, role: PlayerRole, meta: MetaProgression): Player {
  let bonusSalary = getStatEffect(meta, StatUpgradeId.Gehalt);
  let bonusMaxFatigue = getStatEffect(meta, StatUpgradeId.MaxMuedigkeit);
  // Permanent unlock bonuses
  if (meta.permanentUnlocks[PermanentUnlockId.Firmenwagen]) bonusSalary += 100;
  if (meta.permanentUnlocks[PermanentUnlockId.KantineFlatrate]) bonusMaxFatigue += 20;
  return {
    name,
    role,
    salary: STARTING_SALARY + bonusSalary,
    fatigue: 0,
    maxFatigue: MAX_FATIGUE + bonusMaxFatigue,
    reputation: 50,
    meetingsSurvived: 0,
    daysSurvived: 0
  };
}

export function createAchievementTracker(): AchievementTracker {
  return {
    consecutiveSuccessfulProcs: 0,
    consecutiveSilentMeetings: 0,
    spokeUnpromptedThisMeeting: false,
    allCorrectThisMeeting: true,
    questionsThisMeeting: 0,
    monologuesToday: 0,
    wasAddressedThisMeeting: false,
    maxBuzzwordsInMeeting: 0
  };
}

export function createSessionStats(): SessionStats {
  return {
    totalMeetingsSurvived: 0,
    totalDaysSurvived: 0,
    totalTimesCaught: 0,
    totalTimesFellAsleep: 0,
    totalMinigamesWon: 0,
    totalMinigamesLost: 0,
    totalMonologues: 0,
    totalProcrastinations: 0,
    totalSuccessfulProcrastinations: 0,
    totalKaffeeUsed: 0,
    totalCardDrawWins: 0,
    totalBossMeetings: 0,
    meetingTypeStats: {},
    colleagueRoleStats: {},
    bestDaysSurvived: 0,
    bestEndingSalary: 0,
    longestMeetingSurvived: 0
  };
}

/** Merge loaded stats from server with fresh defaults to prevent missing-field crashes */
function sanitizeSessionStats(loaded?: SessionStats): SessionStats {
  const base = createSessionStats();
  if (!loaded) return base;
  return {
    ...base,
    ...loaded,
    meetingTypeStats: (loaded.meetingTypeStats && typeof loaded.meetingTypeStats === 'object') ? loaded.meetingTypeStats : {},
    colleagueRoleStats: (loaded.colleagueRoleStats && typeof loaded.colleagueRoleStats === 'object') ? loaded.colleagueRoleStats : {},
  };
}

export function createGameState(
  player: Player,
  meta: MetaProgression,
  loadedAchievements?: AchievementId[],
  loadedStats?: SessionStats
): GameState {
  return {
    player,
    currentMeeting: null,
    meetingQueue: generateDaySchedule(1),
    currentDay: 1,
    isGameOver: false,
    gameOverReason: '',
    log: [],
    meta,
    activePowerups: [],
    consecutiveCaught: 0,
    dayStats: createDayStats(player.salary),
    activePerks: [],
    daysSinceLastEncounter: 0,
    attestDaysRemaining: 0,
    achievements: new Set(loadedAchievements ?? []),
    achievementTracker: createAchievementTracker(),
    sessionStats: sanitizeSessionStats(loadedStats),

    relationships: {},
    bingoCard: null,
    isWeekendEvent: false,
    weekendEvent: null,
    ending: null,
    soundEnabled: true,
    peakSalary: player.salary
  };
}

export function updatePeakSalary(state: GameState): void {
  if (state.player.salary > state.peakSalary) {
    state.peakSalary = state.player.salary;
  }
}

export function startNextMeeting(state: GameState): Meeting | null {
  if (state.meetingQueue.length === 0) return null;
  const nextType = state.meetingQueue.shift()!;
  const meeting = generateMeeting(nextType, state.currentDay);
  state.currentMeeting = meeting;
  return meeting;
}

export function advanceDay(state: GameState): void {
  state.currentDay++;
  state.player.daysSurvived++;
  state.daysSinceLastEncounter++;
  if (state.attestDaysRemaining > 0) state.attestDaysRemaining--;
  const mods = getPerkModifiers(state);
  const restoreAmount = Math.floor(state.player.maxFatigue * DAY_CHANGE_FATIGUE_RESTORE_PERCENT * mods.dayRecoveryMult);
  state.player.fatigue = Math.max(0, state.player.fatigue - restoreAmount);
  // Networking perk: +salary per day
  if (mods.salaryPerDay !== 0) state.player.salary += mods.salaryPerDay;
  // Mieterhöhung perk
  state.meetingQueue = generateDaySchedule(state.currentDay);
  state.dayStats = createDayStats(state.player.salary);
}

export function isPlayerTurn(meeting: Meeting): boolean {
  return meeting.turnOrder[meeting.currentSpeakerIdx] === meeting.playerSlot;
}

export function getCurrentSlot(meeting: Meeting): number {
  return meeting.turnOrder[meeting.currentSpeakerIdx];
}

/**
 * Any colleague can address the player during their own speaking turn.
 * Lower chance = more subtle. speakingColleagueIdx = the colleague currently talking.
 */
export function checkIfPlayerAddressed(
  meeting: Meeting,
  playerRole: PlayerRole,
  speakingColleagueIdx: number,
  meta?: MetaProgression,
  day?: number,
  relationships?: Record<string, ColleagueRelationship>
): { addressed: boolean; method: AddressMethod; byColleagueIdx: number } {
  let chance = PLAYER_ROLE_DIFFICULTY[playerRole];
  // 1-on-1: always addressed — no escape
  if (meeting.type === MeetingType.OneOnOne) {
    const roll = Math.random();
    const method: AddressMethod = roll < 0.4 ? 'name' : roll < 0.7 ? 'role' : 'informal';
    return { addressed: true, method, byColleagueIdx: speakingColleagueIdx };
  }
  if (meeting.isOnline) chance *= ONLINE_ADDRESS_MODIFIER;
  // Boss meeting: doubled address chance
  if (meeting.type === MeetingType.Vorstandsmeeting) chance *= 2;
  // Day-based difficulty scaling
  if (day !== undefined) chance *= getDifficultyScaling(day).addressChanceMult;
  // Unauffälligkeit reduces address chance
  if (meta) chance *= Math.max(0, 1 - getStatEffect(meta, StatUpgradeId.Unauffaelligkeit));
  // Goldene Badge permanent unlock
  if (meta?.permanentUnlocks[PermanentUnlockId.GoldeneBadge]) chance *= 0.92;
  // Büro-Hund: minor address modifier from lower stress
  // Colleague relationship: friendly = less addressing, hostile = more
  if (relationships) {
    const colleague = meeting.participants[speakingColleagueIdx];
    const colKey = `${colleague.name}_${colleague.role}`;
    const rel = relationships[colKey];
    if (rel && rel.encounters >= 2) {
      // Affinity: -10 to 10. Each point = ±2% address chance
      const relMod = 1 + rel.affinity * -0.02;
      chance *= Math.max(0.5, Math.min(1.5, relMod));
    }
  }
  // Note: perk modifiers are applied in the caller (main.ts) to avoid circular dependency
  const isAddressed = Math.random() < chance;
  if (!isAddressed) return { addressed: false, method: 'informal', byColleagueIdx: speakingColleagueIdx };
  const roll = Math.random();
  const method: AddressMethod = roll < 0.4 ? 'name' : roll < 0.7 ? 'role' : 'informal';
  return { addressed: true, method, byColleagueIdx: speakingColleagueIdx };
}

export function checkDirectionChange(meeting: Meeting): boolean {
  if (Math.random() < 0.04) {
    meeting.directionForward = !meeting.directionForward;
    return true;
  }
  return false;
}

/**
 * Advance to next speaker. Returns true if the round is complete.
 */
export function advanceToNextSpeaker(meeting: Meeting): boolean {
  if (meeting.directionForward) {
    meeting.currentSpeakerIdx++;
  } else {
    meeting.currentSpeakerIdx--;
  }
  if (meeting.currentSpeakerIdx >= meeting.turnOrder.length || meeting.currentSpeakerIdx < 0) {
    meeting.roundsRemaining--;
    meeting.currentSpeakerIdx = meeting.directionForward ? 0 : meeting.turnOrder.length - 1;
    return true;
  }
  return false;
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function processProcrastinate(state: GameState, wasAddressed: boolean): TurnResult {
  const activity = pick(procrastinationActivities());

  // Laptop-Tarnung: immune to being caught
  const hasLaptopTarnung = state.activePowerups.some((p) => p.id === PowerupId.LaptopTarnung && p.remainingTurns > 0);
  // Handy-Akku: one-shot immunity
  const handyIdx = state.activePowerups.findIndex((p) => p.id === PowerupId.HandyAkku);
  const hasHandyAkku = handyIdx >= 0;

  const effectivelyAddressed = wasAddressed && !hasLaptopTarnung && !hasHandyAkku;

  // Consume Handy-Akku if it was relevant
  if (wasAddressed && hasHandyAkku) {
    state.activePowerups.splice(handyIdx, 1);
  }

  if (effectivelyAddressed) {
    let penalty = CAUGHT_SALARY_PENALTY;
    // Escalation: consecutive catches increase penalty
    const escalation = 1 + state.consecutiveCaught * 0.25;
    penalty = Math.round(penalty * escalation);
    // Perk modifiers for salary penalties
    const mods = getPerkModifiers(state);
    penalty = Math.round(penalty * mods.salaryPenaltyMult);
    // Firmen-Kreditkarte: reduce salary penalties
    const kreditReduction = getStatEffect(state.meta, StatUpgradeId.Kreditkarte);
    if (kreditReduction > 0) penalty = Math.max(10, Math.round(penalty * (1 - kreditReduction)));
    state.player.salary -= penalty;
    state.player.reputation = Math.max(0, state.player.reputation - 10);
    state.consecutiveCaught++;
    const turns = C().ui.turns;
    const escalationHint = state.consecutiveCaught > 1 ? turns.escalation(state.consecutiveCaught) : '';
    return {
      message: turns.caught(activity.caughtAction, penalty, escalationHint),
      fatigueDelta: 0,
      salaryDelta: -penalty,
      extraRounds: 0,
      fellAsleep: false,
      caughtProcrastinating: true,
      directionChanged: false
    };
  }

  // Successful procrastination resets escalation
  state.consecutiveCaught = 0;

  let reduction = PROCRASTINATE_FATIGUE_REDUCTION;
  // Day-based difficulty scaling: procrastination less effective over time
  const procScaling = getDifficultyScaling(state.currentDay);
  reduction = Math.round(reduction * procScaling.fatigueReductionMult);
  // Perk modifiers
  const procMods = getPerkModifiers(state);
  reduction = Math.round(reduction * procMods.fatigueReductionMult);
  // Critical hit: double reduction
  const critChance = getStatEffect(state.meta, StatUpgradeId.CriticalHit);
  const isCrit = critChance > 0 && Math.random() < critChance;
  if (isCrit) reduction *= 2;

  state.player.fatigue = Math.max(0, state.player.fatigue - reduction);
  return {
    message: isCrit ? C().ui.turns.procrastinateCritical(activity.action, reduction) : activity.action,
    fatigueDelta: -reduction,
    salaryDelta: 0,
    extraRounds: 0,
    fellAsleep: false,
    caughtProcrastinating: false,
    directionChanged: false
  };
}

export function processUnpromptedAction(state: GameState): TurnResult {
  state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
  if (state.currentMeeting) state.currentMeeting.roundsRemaining += MONOLOGUE_EXTRA_ROUNDS;
  return {
    message: C().ui.turns.monologue(MONOLOGUE_EXTRA_ROUNDS),
    fatigueDelta: 5,
    salaryDelta: 0,
    extraRounds: MONOLOGUE_EXTRA_ROUNDS,
    fellAsleep: false,
    caughtProcrastinating: false,
    directionChanged: false
  };
}

export function applyColleagueTurn(state: GameState, colleagueIdx: number): TurnResult {
  const meeting = state.currentMeeting!;
  const colleague = meeting.participants[colleagueIdx];
  let fatigueCost = colleague.talkiness;
  // Day-based difficulty scaling
  const scaling = getDifficultyScaling(state.currentDay);
  fatigueCost = Math.round(fatigueCost * scaling.fatigueGainMult);
  // Perk modifiers for fatigue gain
  const colMods = getPerkModifiers(state);
  fatigueCost = Math.round(fatigueCost * colMods.fatigueGainMult);
  // Noise-Cancelling: halve fatigue from colleagues
  const hasNC = state.activePowerups.some((p) => p.id === PowerupId.NoiseCancelling);
  if (hasNC) fatigueCost = Math.max(1, Math.floor(fatigueCost * 0.5));
  // Bluetooth-Ohrstöpsel: reduce fatigue per colleague speech
  const ohrstoepselReduction = getStatEffect(state.meta, StatUpgradeId.Ohrstoepsel);
  if (ohrstoepselReduction > 0) fatigueCost = Math.max(1, fatigueCost - ohrstoepselReduction);
  state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + fatigueCost);
  return {
    message: colleague.catchPhrase,
    fatigueDelta: fatigueCost,
    salaryDelta: 0,
    extraRounds: 0,
    fellAsleep: false,
    caughtProcrastinating: false,
    directionChanged: false,
    speakerName: colleague.name,
    speakerRole: colleague.role
  };
}

export function processQuestionAnswer(state: GameState, correct: boolean): TurnResult {
  if (correct) {
    let reduction = FATIGUE_REDUCTION_CORRECT_ANSWER;
    // Critical hit on correct answers
    const critChance = getStatEffect(state.meta, StatUpgradeId.CriticalHit);
    if (critChance > 0 && Math.random() < critChance) reduction *= 2;
    state.player.fatigue = Math.max(0, state.player.fatigue - reduction);
    return {
      message:
        reduction > FATIGUE_REDUCTION_CORRECT_ANSWER
          ? C().ui.turns.answerCritical(reduction)
          : C().ui.turns.answerGood(reduction),
      fatigueDelta: -reduction,
      salaryDelta: 0,
      extraRounds: 0,
      fellAsleep: false,
      caughtProcrastinating: false,
      directionChanged: false
    };
  }
  if (state.currentMeeting) state.currentMeeting.roundsRemaining += WRONG_ANSWER_EXTRA_ROUNDS;
  state.player.fatigue = Math.min(getMaxFatigueWithPerks(state), state.player.fatigue + 5);
  return {
    message: C().ui.turns.answerWrong(WRONG_ANSWER_EXTRA_ROUNDS),
    fatigueDelta: 5,
    salaryDelta: 0,
    extraRounds: WRONG_ANSWER_EXTRA_ROUNDS,
    fellAsleep: false,
    caughtProcrastinating: false,
    directionChanged: false
  };
}

export function tickPowerups(state: GameState): void {
  for (const p of state.activePowerups) {
    if (p.remainingTurns > 0) p.remainingTurns--;
  }
  state.activePowerups = state.activePowerups.filter((p) => p.remainingTurns !== 0);
  // Standing Desk: passive fatigue reduction each player turn
  const deskReduction = getStatEffect(state.meta, StatUpgradeId.StandingDesk);
  if (deskReduction > 0) {
    state.player.fatigue = Math.max(0, state.player.fatigue - deskReduction);
  }
}

export function checkFellAsleep(state: GameState): TurnResult | null {
  const effectiveMax = getMaxFatigueWithPerks(state);
  if (state.player.fatigue >= effectiveMax) {
    let penalty = SLEEP_SALARY_PENALTY;
    // Perk modifiers for salary penalties
    const sleepMods = getPerkModifiers(state);
    penalty = Math.round(penalty * sleepMods.salaryPenaltyMult);
    // Firmen-Kreditkarte: reduce salary penalties
    const kreditReduction = getStatEffect(state.meta, StatUpgradeId.Kreditkarte);
    if (kreditReduction > 0) penalty = Math.max(20, Math.round(penalty * (1 - kreditReduction)));
    state.player.salary -= penalty;
    state.player.fatigue = Math.floor(effectiveMax * 0.5);
    state.player.reputation = Math.max(0, state.player.reputation - 20);
    return {
      message: C().ui.turns.fellAsleep(penalty),
      fatigueDelta: 0,
      salaryDelta: -SLEEP_SALARY_PENALTY,
      extraRounds: 0,
      fellAsleep: true,
      caughtProcrastinating: false,
      directionChanged: false
    };
  }
  return null;
}

export function checkGameOver(state: GameState): boolean {
  if (state.player.salary < MIN_WAGE) {
    state.isGameOver = true;
    state.gameOverReason = C().ui.gameOverReasons.belowMinWage(state.player.salary, MIN_WAGE);
    return true;
  }
  return false;
}

export function completeMeeting(state: GameState): void {
  state.player.meetingsSurvived++;
  // Fatigue recovery for completing a meeting
  const mods = getPerkModifiers(state);
  const reduction = Math.round(MEETING_COMPLETION_FATIGUE_REDUCTION * mods.fatigueReductionMult);
  state.player.fatigue = Math.max(0, state.player.fatigue - reduction);
  state.currentMeeting = null;
}

export function rollForSpecialEvent(): SpecialEventType {
  return rollSpecialEvent();
}

// ---- Day-based Difficulty Scaling ----

/**
 * Returns multipliers that make the game progressively harder.
 * Uses a logarithmic curve: fast early gains that taper off.
 */
export function getDifficultyScaling(day: number): {
  fatigueGainMult: number;
  addressChanceMult: number;
  fatigueReductionMult: number;
} {
  const t = Math.max(0, day - 1);
  // ln(1+t) curve with 0.12 coefficient → Day 5: ~1.19, Day 10: ~1.28, Day 20: ~1.36
  const baseFactor = 1 + 0.12 * Math.log1p(t);
  return {
    fatigueGainMult: baseFactor,
    addressChanceMult: 1 + 0.08 * Math.log1p(t), // gentler for address chance
    fatigueReductionMult: 1 / (1 + 0.015 * t) // procrastination slowly less effective
  };
}

// ---- Perk Modifier Aggregation ----

export function getPerkModifiers(state: GameState): PerkModifiers {
  const mods: PerkModifiers = {
    fatigueGainMult: 1.0,
    fatigueReductionMult: 1.0,
    addressChanceMult: 1.0,
    salaryPenaltyMult: 1.0,
    minigameTimeMult: 1.0,
    salaryPerDay: 0,
    dayRecoveryMult: 1.0
  };
  // Permanent unlock: Büro-Hund
  if (state.meta.permanentUnlocks[PermanentUnlockId.BueroHund]) mods.fatigueGainMult *= 0.95;
  // Permanent unlock: Dienstwohnung
  if (state.meta.permanentUnlocks[PermanentUnlockId.Dienstwohnung]) mods.dayRecoveryMult *= 1.15;
  // Attest: massive bonuses
  if (state.attestDaysRemaining > 0) {
    mods.fatigueGainMult *= 0.3;
    mods.fatigueReductionMult *= 3.0;
    mods.addressChanceMult *= 0.2;
    mods.salaryPenaltyMult *= 0.1;
    mods.salaryPerDay += 100;
  }
  // Powerup: Prompt-Bibliothek — the prompts are ready, so every clock runs longer.
  if (state.activePowerups.some((p) => p.id === PowerupId.PromptBibliothek)) {
    mods.minigameTimeMult *= 1.6;
  }
  for (const perkId of state.activePerks) {
    switch (perkId) {
      case 'guterSchlaf':
        mods.fatigueGainMult *= 0.8;
        break;
      case 'motivationsschub':
        mods.fatigueReductionMult *= 1.5;
        break;
      case 'fluesterModus':
        mods.addressChanceMult *= 0.8;
        break;
      case 'sparsamkeit':
        mods.salaryPenaltyMult *= 0.75;
        break;
      case 'networking':
        mods.salaryPerDay += 50;
        break;
      case 'flowState':
        mods.minigameTimeMult *= 1.25;
        break;
      case 'langeGezockt':
        break; // handled at day start (initial fatigue)
      case 'kater':
        mods.fatigueGainMult *= 1.25;
        break;
      case 'schlechtGeschlafen':
        mods.fatigueReductionMult *= 0.7;
        break;
      case 'auffaellig':
        mods.addressChanceMult *= 1.25;
        break;
      case 'mieterhoehung':
        mods.salaryPerDay -= 30;
        break;
      case 'ablenkung':
        mods.minigameTimeMult *= 0.75;
        break;
      case 'montagsblues':
        mods.dayRecoveryMult *= 0.5;
        break;
      // New perks
      case 'doppelterEspresso':
        mods.fatigueGainMult *= 0.7;
        break;
      case 'chefImUrlaub':
        mods.addressChanceMult *= 0.6;
        break;
      case 'fruehstuecksbuffet':
        mods.salaryPenaltyMult *= 0.85;
        break;
      case 'glueckssocken':
        mods.minigameTimeMult *= 1.4;
        break;
      case 'parkplatzGefunden':
        mods.salaryPerDay += 75;
        break;
      case 'stau':
        mods.salaryPenaltyMult *= 1.2;
        break;
      case 'tastaturDefekt':
        mods.minigameTimeMult *= 0.65;
        break;
      case 'kantineGeschlossen':
        mods.fatigueReductionMult *= 0.5;
        break;
      case 'druckerStau':
        mods.addressChanceMult *= 1.3;
        break;
      case 'windowsUpdate':
        mods.dayRecoveryMult *= 0.3;
        break;
      // The new way of working
      case 'kiCopilot':
        mods.fatigueGainMult *= 0.75;
        break;
      case 'promptGefunden':
        mods.minigameTimeMult *= 1.3;
        break;
      case 'kiAusfall':
        mods.fatigueReductionMult *= 0.6;
        break;
      case 'halluziniertesProtokoll':
        mods.addressChanceMult *= 1.3;
        break;
      case 'agentLaeuft':
        mods.fatigueReductionMult *= 1.4;
        break;
      case 'modellUpdate':
        break; // handled by the fatigue ceiling
      case 'kontextfensterVoll':
        mods.minigameTimeMult *= 0.7;
        break;
      case 'promptInjection':
        mods.addressChanceMult *= 1.35;
        break;
    }
  }
  return mods;
}

export function getMaxFatigueWithPerks(state: GameState): number {
  let max = state.player.maxFatigue;
  if (state.activePerks.includes('zuckerflash')) max += 15;
  if (state.activePerks.includes('fruehstuecksbuffet')) max += 20;
  if (state.activePerks.includes('sodbrennen')) max -= 10;
  if (state.activePerks.includes('kantineGeschlossen')) max -= 15;
  if (state.activePerks.includes('modellUpdate')) max += 20;
  return Math.max(30, max);
}

// ---- Achievement Checking ----

export function checkAndAwardAchievements(state: GameState): AchievementId[] {
  const newlyUnlocked: AchievementId[] = [];
  function tryUnlock(id: AchievementId): void {
    if (!state.achievements.has(id)) {
      state.achievements.add(id);
      newlyUnlocked.push(id);
    }
  }

  const t = state.achievementTracker;
  const ds = state.dayStats;
  const ss = state.sessionStats;

  // Meeting-Marathon: 10 meetings in one day
  if (ds.meetingsSurvived >= 10) tryUnlock(AchievementId.MeetingMarathon);

  // Unsichtbar: meeting without being addressed (checked at meeting end)
  if (!t.wasAddressedThisMeeting) tryUnlock(AchievementId.Unsichtbar);

  // Schlafwandler: 3 falls-asleep in one day, still alive
  if (ds.timesFellAsleep >= 3 && !state.isGameOver) tryUnlock(AchievementId.Schlafwandler);

  // BullshitBingo: 15+ buzzwords in one meeting with bingo card
  if (t.maxBuzzwordsInMeeting >= 15) tryUnlock(AchievementId.BullshitBingo);

  // Unantastbar: 20 consecutive successful procrastinations
  if (t.consecutiveSuccessfulProcs >= 20) tryUnlock(AchievementId.Unantastbar);

  // Monolog-König: 5 monologues in one day
  if (t.monologuesToday >= 5) tryUnlock(AchievementId.MonologKoenig);

  // Speedrunner: all correct in a meeting with >=2 questions
  if (t.allCorrectThisMeeting && t.questionsThisMeeting >= 2) tryUnlock(AchievementId.Speedrunner);

  // Gehaltsverhandler: end day with more salary
  if (ds.salaryEnd > ds.salaryStart) tryUnlock(AchievementId.Gehaltsverhandler);

  // Flüsterer: 3 consecutive silent meetings
  if (t.consecutiveSilentMeetings >= 3) tryUnlock(AchievementId.Fluesterer);

  // Legend: 30 days
  if (state.player.daysSurvived >= 30) tryUnlock(AchievementId.Legend);

  // Koffein-Junkie: 10 kaffee total
  if (ss.totalKaffeeUsed >= 10) tryUnlock(AchievementId.KoffeinJunkie);

  // Teflon-Beschichtung: 5 boss meetings
  if (ss.totalBossMeetings >= 5) tryUnlock(AchievementId.TeflonBeschichtung);

  // Kartenhai: 5 card draw wins
  if (ss.totalCardDrawWins >= 5) tryUnlock(AchievementId.Kartenhai);

  // Minigame-Meister: 20 total minigames won
  if (ss.totalMinigamesWon >= 20) tryUnlock(AchievementId.MinigameMeister);

  // Überlebenskünstler: survive a day with salary < 1200
  if (state.player.salary < 1200 && !state.isGameOver) tryUnlock(AchievementId.Ueberlebenskuenstler);

  return newlyUnlocked;
}

/** Update session stats at end of meeting */
export function updateSessionStatsAfterMeeting(state: GameState): void {
  const meeting = state.currentMeeting;
  if (!meeting) return;
  const ss = state.sessionStats;
  if (!ss.meetingTypeStats) ss.meetingTypeStats = {};
  if (!ss.colleagueRoleStats) ss.colleagueRoleStats = {};
  const typeKey = meeting.type;
  if (!ss.meetingTypeStats[typeKey]) {
    ss.meetingTypeStats[typeKey] = { survived: 0, fellAsleep: 0, caught: 0 };
  }
  ss.meetingTypeStats[typeKey].survived++;
  const meetingSize = meeting.participants.length * meeting.roundsTotal;
  if (meetingSize > ss.longestMeetingSurvived) ss.longestMeetingSurvived = meetingSize;
  if (meeting.type === MeetingType.Vorstandsmeeting) ss.totalBossMeetings++;
}

/** Track colleague fatigue in session stats */
export function trackColleagueFatigue(state: GameState, colleagueRole: string, fatigue: number): void {
  const ss = state.sessionStats;
  if (!ss.colleagueRoleStats) ss.colleagueRoleStats = {};
  if (!ss.colleagueRoleStats[colleagueRole]) {
    ss.colleagueRoleStats[colleagueRole] = { fatigueCaused: 0, encounters: 0 };
  }
  ss.colleagueRoleStats[colleagueRole].fatigueCaused += fatigue;
  ss.colleagueRoleStats[colleagueRole].encounters++;
}

// ---- Colleague Relationships ----

export function updateRelationship(
  state: GameState,
  colleagueName: string,
  colleagueRole: string,
  delta: number
): void {
  const key = `${colleagueName}_${colleagueRole}`;
  if (!state.relationships[key]) {
    state.relationships[key] = { affinity: 0, encounters: 0 };
  }
  const rel = state.relationships[key];
  rel.encounters++;
  rel.affinity = Math.max(-10, Math.min(10, rel.affinity + delta));
}

export function getRelationshipTag(state: GameState, colleagueName: string, colleagueRole: string): string {
  const key = `${colleagueName}_${colleagueRole}`;
  const rel = state.relationships[key];
  if (!rel || rel.encounters < 2) return '';
  if (rel.affinity >= 5) return '💚';
  if (rel.affinity >= 2) return '🙂';
  if (rel.affinity <= -5) return '💢';
  if (rel.affinity <= -2) return '😒';
  return '';
}

// ---- Meeting Bingo ----

export function generateBingoCard(): BingoCard {
  const shuffled = [...BINGO_GOAL_SPECS].sort(() => Math.random() - 0.5);
  return {
    goals: shuffled.slice(0, 4),
    completed: [false, false, false, false]
  };
}

export function checkBingoGoals(state: GameState): void {
  const card = state.bingoCard;
  if (!card) return;
  const ds = state.dayStats;
  for (let i = 0; i < card.goals.length; i++) {
    if (card.completed[i]) continue;
    switch (card.goals[i].check) {
      case 'meetingSurvived':
        card.completed[i] = ds.bingoMeetingSurvived;
        break;
      case 'noSleep':
        card.completed[i] = ds.bingoNoSleep && ds.bingoMeetingSurvived;
        break;
      case 'noCatch':
        card.completed[i] = ds.bingoNoCatch && ds.bingoMeetingSurvived;
        break;
      case 'minigameWon':
        card.completed[i] = ds.bingoMinigameWon;
        break;
      case 'monologue':
        card.completed[i] = ds.bingoMonologue;
        break;
      case 'invisible':
        card.completed[i] = ds.bingoInvisible;
        break;
      case 'questionCorrect':
        card.completed[i] = ds.bingoQuestionCorrect;
        break;
      case 'procrastinated3x':
        card.completed[i] = ds.bingoProcrastinateCount >= 3;
        break;
      case 'directionChange':
        card.completed[i] = ds.bingoDirectionChange;
        break;
    }
  }
}

export function isBingoComplete(state: GameState): boolean {
  return state.bingoCard !== null && state.bingoCard.completed.every((c) => c);
}

// ---- Multiple Endings ----

export function determineEnding(state: GameState): EndingType {
  // Burnout: 5+ falls asleep in one day
  if (state.dayStats.timesFellAsleep >= 5) return EndingType.Burnout;
  // Whistleblower: used 20+ bullshit bingo total
  if (state.meta.totalBullshitBingosUsed >= 20) return EndingType.Whistleblower;
  // Promoted: survived 50+ days
  if (state.player.daysSurvived >= 50) return EndingType.Promoted;
  // Retired: survived 30+ days with > 3000€
  if (state.player.daysSurvived >= 30 && state.player.salary >= 3000) return EndingType.Retired;
  // Default: fired
  return EndingType.Fired;
}

// ---- Homeoffice permanent unlock: chance for auto-online meeting ----

export function rollHomeoffice(meta: MetaProgression): boolean {
  if (!meta.permanentUnlocks[PermanentUnlockId.Homeoffice]) return false;
  return Math.random() < 0.15;
}

// ---- Sound Effects (Web Audio API beeps) ----

let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  return audioCtx;
}

export function playBeep(frequency: number, duration: number, volume = 0.15): void {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch {
    // Audio not supported
  }
}

export function playSoundEffect(type: string, enabled: boolean): void {
  if (!enabled) return;
  switch (type) {
    case 'addressed':
      playBeep(880, 0.15, 0.1);
      break;
    case 'caught':
      playBeep(220, 0.5, 0.15);
      setTimeout(() => playBeep(180, 0.3, 0.1), 150);
      break;
    case 'fellAsleep':
      playBeep(150, 0.8, 0.12);
      break;
    case 'gameOver':
      playBeep(200, 0.3, 0.15);
      setTimeout(() => playBeep(150, 0.4, 0.12), 350);
      setTimeout(() => playBeep(100, 0.8, 0.2), 800);
      break;
    case 'achievement':
      playBeep(660, 0.1, 0.1);
      setTimeout(() => playBeep(880, 0.1, 0.1), 120);
      setTimeout(() => playBeep(1100, 0.2, 0.1), 240);
      break;
    case 'powerUp':
      playBeep(440, 0.1, 0.08);
      setTimeout(() => playBeep(660, 0.15, 0.08), 100);
      break;
    case 'minigameWin':
      playBeep(523, 0.1, 0.08);
      setTimeout(() => playBeep(659, 0.1, 0.08), 100);
      setTimeout(() => playBeep(784, 0.2, 0.08), 200);
      break;
    case 'minigameFail':
      playBeep(350, 0.2, 0.1);
      setTimeout(() => playBeep(250, 0.3, 0.1), 200);
      break;
    case 'vendorAppear':
      playBeep(500, 0.1, 0.08);
      setTimeout(() => playBeep(600, 0.1, 0.08), 150);
      setTimeout(() => playBeep(500, 0.1, 0.08), 300);
      break;
    case 'bingoComplete':
      playBeep(440, 0.08, 0.08);
      setTimeout(() => playBeep(550, 0.08, 0.08), 100);
      setTimeout(() => playBeep(660, 0.08, 0.08), 200);
      setTimeout(() => playBeep(880, 0.3, 0.12), 300);
      break;
    // ---- New sound effects ----
    case 'colleagueSpeak':
      // Short blip for colleague talking — varies pitch slightly each call
      playBeep(300 + Math.random() * 100, 0.06, 0.04);
      break;
    case 'playerTurn':
      // Subtle double-tick to signal it's the player's turn
      playBeep(600, 0.05, 0.06);
      setTimeout(() => playBeep(700, 0.05, 0.06), 80);
      break;
    case 'procrastinateSuccess':
      // Sneaky low slide down
      playBeep(400, 0.08, 0.05);
      setTimeout(() => playBeep(350, 0.1, 0.04), 100);
      break;
    case 'monologue':
      // Awkward ascending blip
      playBeep(250, 0.08, 0.05);
      setTimeout(() => playBeep(300, 0.08, 0.05), 100);
      setTimeout(() => playBeep(200, 0.12, 0.04), 200);
      break;
    case 'directionChange':
      // Quick swoosh (high-to-low)
      playBeep(700, 0.06, 0.06);
      setTimeout(() => playBeep(500, 0.06, 0.06), 70);
      setTimeout(() => playBeep(350, 0.08, 0.06), 140);
      break;
    case 'dayStart':
      // Morning chime
      playBeep(523, 0.08, 0.07);
      setTimeout(() => playBeep(659, 0.08, 0.07), 120);
      setTimeout(() => playBeep(784, 0.08, 0.07), 240);
      setTimeout(() => playBeep(1047, 0.15, 0.07), 360);
      break;
    case 'daySummary':
      // Calm end-of-day jingle
      playBeep(784, 0.1, 0.06);
      setTimeout(() => playBeep(659, 0.1, 0.06), 150);
      setTimeout(() => playBeep(523, 0.15, 0.06), 300);
      break;
    case 'meetingStart':
      // Door opening — two knocks
      playBeep(200, 0.04, 0.08);
      setTimeout(() => playBeep(200, 0.04, 0.08), 120);
      break;
    case 'meetingEnd':
      // Relief sigh — descending
      playBeep(500, 0.06, 0.05);
      setTimeout(() => playBeep(440, 0.06, 0.05), 100);
      setTimeout(() => playBeep(350, 0.1, 0.04), 200);
      break;
    case 'encounter':
      // Mysterious encounter chime
      playBeep(440, 0.08, 0.06);
      setTimeout(() => playBeep(550, 0.08, 0.06), 120);
      setTimeout(() => playBeep(440, 0.1, 0.05), 250);
      break;
    case 'positiveEvent':
      // Happy little jingle
      playBeep(523, 0.06, 0.06);
      setTimeout(() => playBeep(659, 0.06, 0.06), 80);
      setTimeout(() => playBeep(784, 0.1, 0.07), 160);
      break;
    case 'salaryPenalty':
      // Cash register "ding" then drop
      playBeep(800, 0.04, 0.08);
      setTimeout(() => playBeep(300, 0.12, 0.06), 80);
      break;
    case 'salaryBonus':
      // Cash register happy "ka-ching"
      playBeep(600, 0.04, 0.06);
      setTimeout(() => playBeep(900, 0.06, 0.06), 60);
      setTimeout(() => playBeep(1200, 0.08, 0.06), 120);
      break;
    case 'timeout':
      // Ticking clock runs out
      playBeep(400, 0.04, 0.05);
      setTimeout(() => playBeep(400, 0.04, 0.05), 100);
      setTimeout(() => playBeep(300, 0.08, 0.04), 200);
      break;
    case 'cardDraw':
      // Card flip
      playBeep(500, 0.03, 0.06);
      setTimeout(() => playBeep(700, 0.05, 0.05), 60);
      break;
    case 'perkPositive':
      // Happy sparkle
      playBeep(800, 0.06, 0.05);
      setTimeout(() => playBeep(1000, 0.08, 0.05), 100);
      break;
    case 'perkNegative':
      // Ominous thud
      playBeep(180, 0.15, 0.06);
      setTimeout(() => playBeep(150, 0.08, 0.04), 150);
      break;
  }
}
